import AppContext from "PFApp/app_context";
import { Page, PageAction } from "PFReducers/page_reducer";
import { useCallback, useContext } from "react";

/**
 * Access the store for sharing the current pages state
 * @param {*} key - Page identifier
 * @param {*} initPage - Initial state of a page on load
 * @returns {Object} - Object of access methods used to interact with the store
 */

type UsePageReturn<PAGE_BEAN extends object> = {
  page: Page<PAGE_BEAN>;
  setPage: (payload?: Page<PAGE_BEAN>) => void;
  mergePage: (payload?: Partial<Page<PAGE_BEAN>>) => void;
  resetPage: () => void;
};

export const usePage = <PAGE_BEAN extends object>(key: string, initPage = {}): UsePageReturn<PAGE_BEAN> => {
  const {
    store: { pages },
    dispatch
  } = useContext(AppContext);

  const actualPage: Page<PAGE_BEAN> = (pages || {})[key] || initPage || {};

  const setPage = useCallback(
    (payload?: Page<PAGE_BEAN>) =>
      dispatch<PageAction<PAGE_BEAN>>({
        type: "PAGE_SET",
        payload: { key, payload }
      }),
    [key]
  );

  const mergePage = useCallback(
    (payload?: Partial<Page<PAGE_BEAN>>) =>
      dispatch<PageAction<PAGE_BEAN>>({
        type: "PAGE_MERGE",
        payload: { key, payload }
      }),
    [key]
  );

  const resetPage = useCallback(
    () =>
      dispatch<PageAction<PAGE_BEAN>>({
        type: "PAGE_RESET",
        payload: { key }
      }),
    [key]
  );

  return {
    page: actualPage,
    setPage,
    mergePage,
    resetPage
  };
};
